import { useState, useEffect, useRef } from 'react'
import VoiceTextarea from '@/components/VoiceTextarea'
import {
  Table, Button, Tag, Space, Badge, Modal, Form,
  Input, Select, message, Empty, Tooltip, Tabs, Spin, Avatar,
} from 'antd'
import {
  PlusOutlined, InboxOutlined, SendOutlined, EyeOutlined, UserOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import axiosInstance from '@/lib/axios'
import { useAuth } from '@/contexts/AuthContext'
import {
  useInbox,
  useInviati,
  useInviaMessaggioInterno,
  useMarcaLetta,
  useThreadInterno,
  useRispondiInterno,
  type MessaggioInterno,
} from '@/hooks/useMessaggiInterni'

interface UserMin {
  id: number
  username: string
  nome?: string
  cognome?: string
}

interface Role {
  id: number
  codice: string
  nome: string
}

function nomeUtente(u?: { nome?: string; cognome?: string; username: string } | null) {
  if (!u) return '—'
  return `${u.nome || ''} ${u.cognome || ''}`.trim() || u.username
}

export function MessaggiInterni() {
  const { user: me } = useAuth()
  const [composeOpen, setComposeOpen] = useState(false)
  const [threadMsgId, setThreadMsgId] = useState<number | null>(null)
  const [replyText, setReplyText] = useState('')
  const [form] = Form.useForm()
  const [tipo, setTipo] = useState<'direct' | 'broadcast'>('direct')
  const [users, setUsers] = useState<UserMin[]>([])
  const [roles, setRoles] = useState<Role[]>([])
  const bottomRef = useRef<HTMLDivElement>(null)

  const { data: inbox = [], isLoading: loadingInbox } = useInbox()
  const { data: inviati = [], isLoading: loadingInviati } = useInviati()
  const { data: thread = [], isLoading: threadLoading } = useThreadInterno(threadMsgId ?? undefined)
  const inviaMut = useInviaMessaggioInterno()
  const rispondiMut = useRispondiInterno()
  const marcaMut = useMarcaLetta()

  useEffect(() => {
    Promise.all([
      axiosInstance.get('/messaggi-interni/users').catch(() => ({ data: [] })),
      axiosInstance.get('/permissions/roles').catch(() => ({ data: [] })),
    ]).then(([u, r]) => {
      setUsers(u.data)
      setRoles(r.data)
    })
  }, [])

  useEffect(() => {
    if (thread.length > 0) {
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50)
    }
  }, [thread.length])

  const handleOpenDetail = (msg: MessaggioInterno) => {
    setThreadMsgId(msg.id)
    setReplyText('')
    if (!msg.letto) {
      marcaMut.mutate(msg.id)
    }
  }

  const handleSend = async (values: any) => {
    try {
      await inviaMut.mutateAsync({
        tipo: values.tipo,
        destinatario_id: values.tipo === 'direct' ? values.destinatario_id : undefined,
        destinatario_ruolo_id: values.tipo === 'broadcast' ? values.destinatario_ruolo_id ?? undefined : undefined,
        oggetto: values.oggetto,
        testo: values.testo,
      })
      message.success('Messaggio inviato')
      setComposeOpen(false)
      form.resetFields()
      setTipo('direct')
    } catch {
      message.error('Errore invio')
    }
  }

  const handleReply = async () => {
    if (!replyText.trim() || !threadMsgId) return
    try {
      await rispondiMut.mutateAsync({ id: threadMsgId, testo: replyText.trim() })
      setReplyText('')
    } catch {
      message.error('Errore invio risposta')
    }
  }

  const threadRoot = thread[0]

  const nonLetti = inbox.filter(m => !m.letto).length

  const inboxColumns = [
    {
      title: 'Da',
      key: 'da',
      width: 160,
      render: (_: any, r: MessaggioInterno) => (
        <span style={{ fontWeight: r.letto ? 400 : 700 }}>{nomeUtente(r.mittente)}</span>
      ),
    },
    {
      title: 'Oggetto / Testo',
      key: 'testo',
      render: (_: any, r: MessaggioInterno) => (
        <div>
          {r.oggetto && (
            <div style={{ fontWeight: r.letto ? 400 : 700, fontSize: 13 }}>{r.oggetto}</div>
          )}
          <div
            style={{
              color: 'var(--color-text-secondary)',
              fontSize: 12,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              maxWidth: 300,
            }}
          >
            {r.testo}
          </div>
        </div>
      ),
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      key: 'tipo',
      width: 100,
      render: (v: string) => (
        <Tag color={v === 'broadcast' ? 'purple' : 'blue'}>
          {v === 'broadcast' ? 'Broadcast' : 'Diretto'}
        </Tag>
      ),
    },
    {
      title: 'Data',
      dataIndex: 'created_at',
      key: 'data',
      width: 130,
      render: (v: string) => dayjs(v).format('DD/MM/YY HH:mm'),
    },
    {
      title: '',
      key: 'azioni',
      width: 60,
      render: (_: any, r: MessaggioInterno) => (
        <Tooltip title="Leggi">
          <Button size="small" icon={<EyeOutlined />} onClick={() => handleOpenDetail(r)} />
        </Tooltip>
      ),
    },
  ]

  const inviatiColumns = [
    {
      title: 'A',
      key: 'a',
      width: 160,
      render: (_: any, r: MessaggioInterno) => {
        if (r.tipo === 'broadcast') {
          const ruolo = roles.find(ro => ro.id === r.destinatario_ruolo_id)
          return <Tag color="purple">{ruolo ? ruolo.nome : 'Tutti'}</Tag>
        }
        return nomeUtente(r.destinatario)
      },
    },
    {
      title: 'Oggetto / Testo',
      key: 'testo',
      render: (_: any, r: MessaggioInterno) => (
        <div>
          {r.oggetto && <div style={{ fontWeight: 600, fontSize: 13 }}>{r.oggetto}</div>}
          <div style={{ color: 'var(--color-text-secondary)', fontSize: 12 }}>{r.testo}</div>
        </div>
      ),
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      key: 'tipo',
      width: 100,
      render: (v: string) => (
        <Tag color={v === 'broadcast' ? 'purple' : 'blue'}>
          {v === 'broadcast' ? 'Broadcast' : 'Diretto'}
        </Tag>
      ),
    },
    {
      title: 'Data',
      dataIndex: 'created_at',
      key: 'data',
      width: 130,
      render: (v: string) => dayjs(v).format('DD/MM/YY HH:mm'),
    },
    {
      title: '',
      key: 'azioni',
      width: 60,
      render: (_: any, r: MessaggioInterno) => (
        <Tooltip title="Leggi">
          <Button size="small" icon={<EyeOutlined />} onClick={() => handleOpenDetail(r)} />
        </Tooltip>
      ),
    },
  ]

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setComposeOpen(true)}>
          Nuovo messaggio interno
        </Button>
      </div>

      <Tabs
        items={[
          {
            key: 'inbox',
            label: (
              <Space>
                <InboxOutlined />
                Ricevuti
                {nonLetti > 0 && <Badge count={nonLetti} size="small" />}
              </Space>
            ),
            children: (
              <Table
                columns={inboxColumns}
                dataSource={inbox}
                rowKey="id"
                loading={loadingInbox}
                size="small"
                pagination={{ pageSize: 20 }}
                locale={{ emptyText: <Empty description="Nessun messaggio ricevuto" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                rowClassName={(r) => (r.letto ? '' : 'ant-table-row-selected')}
                onRow={(record) => ({ onDoubleClick: () => handleOpenDetail(record) })}
              />
            ),
          },
          {
            key: 'inviati',
            label: (
              <Space>
                <SendOutlined />
                Inviati
              </Space>
            ),
            children: (
              <Table
                columns={inviatiColumns}
                dataSource={inviati}
                rowKey="id"
                loading={loadingInviati}
                size="small"
                pagination={{ pageSize: 20 }}
                locale={{ emptyText: <Empty description="Nessun messaggio inviato" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                onRow={(record) => ({ onDoubleClick: () => handleOpenDetail(record) })}
              />
            ),
          },
        ]}
      />

      {/* Compose modal */}
      <Modal
        title="Nuovo messaggio interno"
        open={composeOpen}
        onCancel={() => { setComposeOpen(false); form.resetFields(); setTipo('direct') }}
        footer={null}
        width="min(560px, 95vw)"
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSend} style={{ marginTop: 8 }}>
          <Form.Item name="tipo" label="Tipo" initialValue="direct">
            <Select onChange={(v) => setTipo(v)}>
              <Select.Option value="direct">Messaggio diretto</Select.Option>
              <Select.Option value="broadcast">Broadcast (a tutti o per ruolo)</Select.Option>
            </Select>
          </Form.Item>

          {tipo === 'direct' && (
            <Form.Item
              name="destinatario_id"
              label="Destinatario"
              rules={[{ required: true, message: 'Seleziona un destinatario' }]}
            >
              <Select
                showSearch
                filterOption={(input, opt) =>
                  String(opt?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={users.map(u => ({
                  value: u.id,
                  label: `${u.nome || ''} ${u.cognome || ''}`.trim() || u.username,
                }))}
                placeholder="Cerca utente..."
              />
            </Form.Item>
          )}

          {tipo === 'broadcast' && (
            <Form.Item name="destinatario_ruolo_id" label="Ruolo destinatario (vuoto = tutti)">
              <Select allowClear placeholder="Tutti gli utenti">
                {roles.map(r => (
                  <Select.Option key={r.id} value={r.id}>
                    {r.nome}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item name="oggetto" label="Oggetto">
            <Input placeholder="Oggetto del messaggio (opzionale)" />
          </Form.Item>

          <Form.Item
            name="testo"
            label="Messaggio"
            rules={[{ required: true, message: 'Inserisci il testo' }]}
          >
            <VoiceTextarea rows={4} minHeight={100} maxHeight={250} label="Messaggio" compact />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button onClick={() => { setComposeOpen(false); form.resetFields(); setTipo('direct') }}>
                Annulla
              </Button>
              <Button type="primary" htmlType="submit" loading={inviaMut.isPending}>
                Invia
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* Thread / chat modal */}
      <Modal
        title={threadRoot?.oggetto || 'Conversazione'}
        open={!!threadMsgId}
        onCancel={() => { setThreadMsgId(null); setReplyText('') }}
        footer={null}
        width="min(600px, 95vw)"
        styles={{ body: { padding: '12px 16px' } }}
      >
        {threadLoading ? (
          <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {/* Messaggi thread */}
            <div style={{ maxHeight: 400, overflowY: 'auto', marginBottom: 12, paddingRight: 4 }}>
              {thread.map((msg) => {
                const isMine = msg.mittente_id === me?.id
                return (
                  <div
                    key={msg.id}
                    style={{
                      display: 'flex',
                      flexDirection: isMine ? 'row-reverse' : 'row',
                      alignItems: 'flex-end',
                      gap: 8,
                      marginBottom: 12,
                    }}
                  >
                    <Avatar size={28} icon={<UserOutlined />} style={{ flexShrink: 0, background: isMine ? '#1677ff' : '#8c8c8c' }} />
                    <div style={{ maxWidth: '75%' }}>
                      <div
                        style={{
                          fontSize: 11,
                          color: 'var(--color-text-secondary)',
                          marginBottom: 2,
                          textAlign: isMine ? 'right' : 'left',
                        }}
                      >
                        {nomeUtente(msg.mittente)} · {dayjs(msg.created_at).format('DD/MM HH:mm')}
                      </div>
                      <div
                        style={{
                          background: isMine ? '#1677ff' : 'var(--color-bg-subtle)',
                          color: isMine ? '#fff' : 'var(--color-text)',
                          padding: '8px 12px',
                          borderRadius: isMine ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                          whiteSpace: 'pre-wrap',
                          fontSize: 13,
                          lineHeight: 1.5,
                        }}
                      >
                        {msg.testo}
                      </div>
                    </div>
                  </div>
                )
              })}
              <div ref={bottomRef} />
            </div>

            {/* Box risposta */}
            <div style={{ display: 'flex', gap: 8, borderTop: '1px solid var(--color-border)', paddingTop: 12 }}>
              <VoiceTextarea
                rows={2}
                minHeight={60}
                maxHeight={150}
                placeholder="Scrivi una risposta…"
                label="Risposta"
                compact
                value={replyText}
                onChange={(v) => setReplyText(v)}
              />
              <Button
                type="primary"
                icon={<SendOutlined />}
                loading={rispondiMut.isPending}
                disabled={!replyText.trim()}
                onClick={handleReply}
                style={{ alignSelf: 'flex-end' }}
              >
                Invia
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
